import { Select } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import type { AdminResponses } from "@tfg/types/endpoints/admin"

import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

import type { PlanSettingConfigItem } from "../../config"
import { useUpdateOrganizationMutation } from "../../hooks"

type PlanSettingSelectProps = {
  readonly organization: AdminResponses.OrganizationDetails
  readonly planSettingConfig: PlanSettingConfigItem
  readonly label: string
}

export function PlanSettingSelect({
  organization,
  planSettingConfig,
  label,
}: PlanSettingSelectProps) {
  const { t } = useTranslation("admin")
  const { mutate: updateOrganization, isPending } =
    useUpdateOrganizationMutation(organization.id.toString())
  const currentPlan = organization.settings?.[planSettingConfig.planKey] ?? null
  const [selectedPlan, setSelectedPlan] = useState<string | null>(currentPlan)

  useEffect(() => {
    setSelectedPlan(currentPlan)
  }, [currentPlan])

  return (
    <Select
      inlineLabel
      label={label}
      data={planSettingConfig.options.map((option) => ({
        label: t(option.labelTranslationKey, {
          defaultValue: option.labelTranslationKey,
        }),
        value: option.value,
      }))}
      value={selectedPlan}
      disabled={isPending}
      isLoading={isPending}
      sort={false}
      onChange={(newPlan) => {
        if (!newPlan || newPlan === selectedPlan) return
        const previousPlan = selectedPlan
        setSelectedPlan(newPlan)
        updateOrganization(
          Object.fromEntries([[planSettingConfig.planKey, newPlan]]),
          {
            onSuccess: () => {
              showSuccessNotification({
                message: t("featureAccess.subscriptionPlans.updateSuccess", {
                  defaultValue: "Plan setting updated",
                }),
              })
            },
            onError: (error) => {
              setSelectedPlan(previousPlan)
              showAxiosError({
                error,
                showNotification: true,
                defaultMessage: t(
                  "featureAccess.subscriptionPlans.updateErrorMessage",
                  {
                    defaultValue:
                      "An error occurred while updating plan setting",
                  }
                ),
                notificationTitle: t(
                  "featureAccess.subscriptionPlans.updateErrorTitle",
                  {
                    defaultValue: "Error",
                  }
                ),
              })
            },
          }
        )
      }}
    />
  )
}
